import { Link } from 'react-router-dom';
import { useState } from 'react';
import { MOVE_REASONS, countCash, todayIn } from '@njpos/core';
import { api, loadSession, usd } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';
import { CountForm, DrawerStatement, MoveForm } from '../components/Drawer';

type Tab = 'start' | 'today' | 'journal' | 'past';
const reasonLabel = (r: string) => MOVE_REASONS.find((m) => m.reason === r)?.label ?? r;

// Back office for cash: every cashier's drawer, the cash journal, and finishing the day.
export default function Cash() {
  const [tab, setTab] = useState<Tab>('start');
  const [date, setDate] = useState(todayIn());
  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>Start and end of day</h1><p>Start the day by giving each cashier their starting cash. At closing, balance each drawer, record cash moved in and out, then finish the day.</p></div>
        {tab !== 'past' && <label>Day<input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>}
        <Link to="/checks?tab=drawers">Over and short by cashier</Link>
      </div>
      <div className="row wrap" role="tablist">
        {([['start', 'Start of day'], ['today', 'End of day'], ['journal', 'Cash journal'], ['past', 'Finished days']] as [Tab, string][]).map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'primary' : ''} onClick={() => setTab(k)}>{l}</button>)}
      </div>
      {tab === 'start' && <StartOfDay date={date} />}
      {tab === 'today' && <Today date={date} />}
      {tab === 'journal' && <Journal date={date} />}
      {tab === 'past' && <Past />}
    </div>
  );
}

const catName = (c: string) => c.replace('_', ' ').replace(/^./, (x) => x.toUpperCase());

// Each category's sales and sales tax. Days finished before tax was kept per category show a dash.
function CategoryTable({ categories, sales }: { categories: any[]; sales: any }) {
  const hasTax = categories.every((c) => c.tax_cents != null);
  return (
    <table className="grid">
      <thead><tr><th>Category</th><th className="n">Units</th><th className="n">Sales</th><th className="n">Tax</th></tr></thead>
      <tbody>
        {categories.map((c) => <tr key={c.category}><td>{catName(c.category)}</td><td className="n">{c.units}</td><td className="n">{usd(c.net_cents)}</td><td className="n">{hasTax ? usd(c.tax_cents) : '—'}</td></tr>)}
        <tr style={{ fontWeight: 700 }}><td>All sales</td><td className="n">{categories.reduce((s, c) => s + c.units, 0)}</td><td className="n">{usd(sales.net)}</td><td className="n">{usd(sales.tax)}</td></tr>
      </tbody>
    </table>
  );
}

// Start of the day: see how yesterday ended, then give each cashier their starting cash.
function StartOfDay({ date }: { date: string }) {
  const sod = useLoad(() => api<any>(`/api/start-of-day?date=${date}`), [date]);
  const me = loadSession()?.user.id;
  const [who, setWho] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const d = sod.data;
  const target = who || me || '';
  async function open(openingCents: number) {
    setErr(null); setDone(''); setBusy(true);
    try {
      await api('/api/drawer/open', { body: { openingCents, ...(target && target !== me ? { forUserId: target } : {}) } });
      setDone(`Drawer opened with ${usd(openingCents)}.`); sod.reload();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }
  return (
    <div className="stack">
      <ErrorNote error={sod.error ?? err} />
      {done && <div className="notice good">{done}</div>}
      {d && (
        <>
          {d.finished && <div className="notice">This day has already been finished. An owner can reopen it from End of day.</div>}
          {d.staleOpen.length > 0 && <div className="notice bad">{d.staleOpen.map((x: any) => x.cashier).join(', ')} still ha{d.staleOpen.length === 1 ? 's' : 've'} a drawer open from an earlier day. Close {d.staleOpen.length === 1 ? 'it' : 'them'} under End of day first.</div>}
          {d.lastDay && <div className="faint">Last finished day, {d.lastDay.date}: net sales {usd(d.lastDay.net)} · over / short {usd(d.lastDay.variance)} · deposit {usd(d.lastDay.deposit)}</div>}
          <div className="panel"><div className="panel-head"><h2>Open a drawer</h2></div>
            <div className="panel-body stack">
              <label>Cashier<select value={target} onChange={(e) => setWho(e.target.value)}>
                {d.staff.map((u: any) => <option key={u.id} value={u.id} disabled={u.hasOpen}>{u.name}{u.id === me ? ' (you)' : ''}{u.hasOpen ? ' · drawer already open' : ''}</option>)}
              </select></label>
              {d.suggestedCents != null && <div className="row" style={{ gap: 8 }}><span>Usual starting cash: <b>{usd(d.suggestedCents)}</b></span><button disabled={busy || d.finished} onClick={() => open(d.suggestedCents)}>Open with {usd(d.suggestedCents)}</button></div>}
              <details open={d.suggestedCents == null}><summary>Count the starting cash</summary>
                <CountForm label="Open drawer with this count" busy={busy || d.finished} onSubmit={(counts, other) => open(countCash(counts, other))} />
              </details>
            </div>
          </div>
          <div className="panel"><div className="panel-head"><h2>Drawers today</h2></div>
            <table className="grid"><thead><tr><th>Cashier</th><th>Opened</th><th className="n">Starting cash</th><th>Status</th></tr></thead>
              <tbody>{d.today.map((x: any) => <tr key={x.id}><td>{x.cashier}</td><td>{new Date(x.openedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</td><td className="n">{usd(x.openingCents)}</td>
                <td><span className={`chip ${x.status === 'open' ? 'warn' : 'good'}`}>{x.status === 'open' ? 'open' : 'closed'}</span></td></tr>)}</tbody></table>
            {d.today.length === 0 && <div className="empty">No drawers opened yet today.</div>}
          </div>
        </>
      )}
    </div>
  );
}

function Today({ date }: { date: string }) {
  const day = useLoad(() => api<any>(`/api/day?date=${date}`), [date]);
  const [view, setView] = useState<any>(null);
  const [closing, setClosing] = useState<any>(null);
  const [finishing, setFinishing] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const isOwner = loadSession()?.user.role === 'owner';
  const d = day.data;
  async function closeFor(counts: any, otherCents: number, note: string) {
    setErr(null);
    try { setView(await api(`/api/drawer/${closing.id}/close`, { body: { counts, otherCents, note: note || undefined } })); setClosing(null); day.reload(); } catch (e) { setErr(e); }
  }
  return (
    <div className="stack">
      <ErrorNote error={day.error ?? err} />
      {d && (
        <>
          <div className="stats">
            <div><div className="v">{usd(d.sales.net)}</div><div className="l">Net sales ({d.sales.count} sales)</div></div>
            <div><div className="v">{usd(d.sales.tax)}</div><div className="l">Sales tax</div></div>
            <div><div className="v">{usd(d.cashCountedCents)}</div><div className="l">Cash counted</div></div>
            <div><div className="v" style={{ color: d.varianceCents < 0 ? 'var(--bad)' : undefined }}>{usd(d.varianceCents)}</div><div className="l">Over / short</div></div>
          </div>
          <div className="panel"><div className="panel-head"><h2>Drawers</h2></div>
            <table className="grid"><thead><tr><th>Cashier</th><th>Opened</th><th className="n">Starting</th><th className="n">Should be</th><th className="n">Counted</th><th className="n">Over / short</th><th /></tr></thead>
              <tbody>{d.drawers.map((x: any) => (
                <tr key={x.id}>
                  <td>{x.cashier}</td><td>{new Date(x.openedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</td><td className="n">{usd(x.opening)}</td>
                  <td className="n">{x.status === 'closed' ? usd(x.expected) : '—'}</td><td className="n">{x.status === 'closed' ? usd(x.counted) : <span className="chip warn">open</span>}</td>
                  <td className="n">{x.status === 'closed' ? <span className={`chip ${x.variance === 0 ? 'good' : 'bad'}`}>{x.variance === 0 ? 'balanced' : usd(x.variance)}</span> : ''}</td>
                  <td>{x.status === 'open' ? <button onClick={() => setClosing(x)}>Close for them</button> : <button onClick={async () => { const r = (await api<any[]>(`/api/drawers?date=${date}`)).find((y) => y.id === x.id); setView(r?.statement); }}>Statement</button>}</td>
                </tr>))}</tbody></table>
            {d.drawers.length === 0 && <div className="empty">No drawers were opened this day.</div>}
          </div>
          <div className="grid-2" style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            <div className="panel"><div className="panel-head"><h2>Money in by type</h2></div>
              <table className="grid"><tbody>
                {d.tenders.map((t: any) => <tr key={t.method}><td>{t.method.replace('_', ' ')}</td><td className="n">{usd(t.cents)}</td></tr>)}
                <tr><td>Lottery sales (owed to the state)</td><td className="n">{usd(d.lottery.sales)}</td></tr>
                <tr><td>Lottery paid out</td><td className="n">{usd(-d.lottery.payouts)}</td></tr>
              </tbody></table></div>
            <div className="panel"><div className="panel-head"><h2>Sales by type</h2></div>
              <CategoryTable categories={d.categories} sales={d.sales} />
              <div className="faint" style={{ padding: 12 }}>Discounts {usd(d.sales.discounts)} · refunds {d.sales.refunds} · cancelled {d.cancelledSales}</div>
            </div>
          </div>
          <div className="row" style={{ display: 'flex', gap: 8 }}>
            {d.finished
              ? <><div className="notice good grow">This day has been finished.</div>{isOwner && <button onClick={async () => { if (confirm('Reopen this day?')) { try { await api('/api/day/reopen', { body: { date } }); day.reload(); } catch (e) { setErr(e); } } }}>Reopen</button>}</>
              : <button className="primary big" onClick={() => setFinishing(true)}>Finish the day</button>}
            <button onClick={() => window.print()}>Print end-of-day report</button>
          </div>
        </>
      )}
      {view && <Modal title="Balance statement" onClose={() => setView(null)} wide><DrawerStatement s={view} /></Modal>}
      {closing && <Modal title={`Close ${closing.cashier}'s drawer`} onClose={() => setClosing(null)} wide><CountForm onSubmit={closeFor} /></Modal>}
      {finishing && d && <Finish date={date} d={d} onClose={() => setFinishing(false)} onDone={() => { setFinishing(false); day.reload(); }} />}
    </div>
  );
}

function Finish({ date, d, onClose, onDone }: { date: string; d: any; onClose: () => void; onDone: () => void }) {
  const [deposit, setDeposit] = useState((d.cashCountedCents / 100).toFixed(2));
  const [note, setNote] = useState('');
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title={`Finish ${date}`} onClose={onClose} footer={<button className="primary" disabled={d.openDrawers > 0} onClick={async () => {
      try { await api('/api/day/close', { body: { date, cashToDepositCents: Math.round(Number(deposit || '0') * 100), note: note || undefined } }); onDone(); } catch (e) { setErr(e); }
    }}>Finish the day</button>}>
      <ErrorNote error={err} />
      {d.openDrawers > 0 && <div className="notice bad">{d.openDrawers} drawer{d.openDrawers === 1 ? ' is' : 's are'} still open. Balance them first.</div>}
      <p>Net sales <b>{usd(d.sales.net)}</b>, tax <b>{usd(d.sales.tax)}</b>, over / short <b>{usd(d.varianceCents)}</b>.</p>
      <div className="field-row">
        <label>Cash to deposit<input className="num-input" inputMode="decimal" value={deposit} onChange={(e) => setDeposit(e.target.value.replace(/[^\d.]/g, ''))} /></label>
        <label>Note<input value={note} onChange={(e) => setNote(e.target.value)} /></label>
      </div>
      <p className="faint">Finishing keeps a copy of today's totals. Record the bank deposit itself in the cash journal as a bank deposit.</p>
    </Modal>
  );
}

function Journal({ date }: { date: string }) {
  const moves = useLoad(() => api<any[]>(`/api/cash-moves?date=${date}`), [date]);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  return (
    <div className="stack">
      <div className="row"><button className="primary" onClick={() => setAdding(true)}>Record paid in / out</button></div>
      <ErrorNote error={moves.error ?? err} />
      <div className="panel">
        <table className="grid"><thead><tr><th>Time</th><th>What for</th><th>By</th><th>Note</th><th className="n">Amount</th><th /></tr></thead>
          <tbody>{moves.data?.map((m) => (
            <tr key={m.id} style={m.voided ? { opacity: 0.45 } : undefined}>
              <td>{new Date(m.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</td><td>{reasonLabel(m.reason)}</td><td>{m.by_name}</td><td>{m.note}</td>
              <td className="n">{m.kind === 'in' ? '+' : '−'}{usd(m.amount_cents)}</td>
              <td>{!m.voided && <button onClick={async () => { if (confirm('Void this entry?')) { setErr(null); try { await api(`/api/cash-moves/${m.id}/void`, { body: {} }); moves.reload(); } catch (e) { setErr(e); } } }}>Void</button>}</td>
            </tr>))}</tbody></table>
        {moves.data?.length === 0 && <div className="empty">Nothing recorded this day.</div>}
      </div>
      {adding && <Modal title="Paid in / paid out" onClose={() => setAdding(false)}><MoveForm onDone={() => { setAdding(false); moves.reload(); }} /></Modal>}
    </div>
  );
}

function Past() {
  const days = useLoad(() => api<any[]>('/api/days'), []);
  const [open, setOpen] = useState<any>(null);
  return (
    <div className="panel">
      <table className="grid"><thead><tr><th>Day</th><th>Finished by</th><th className="n">Net sales</th><th className="n">Cash counted</th><th className="n">Over / short</th><th className="n">To deposit</th><th /></tr></thead>
        <tbody>{days.data?.map((d) => (
          <tr key={d.id}><td>{d.date}</td><td>{d.closed_by}</td><td className="n">{usd(d.statement.sales.net)}</td><td className="n">{usd(d.statement.cashCountedCents)}</td>
            <td className="n"><span className={`chip ${d.statement.varianceCents === 0 ? 'good' : 'bad'}`}>{d.statement.varianceCents === 0 ? 'balanced' : usd(d.statement.varianceCents)}</span></td>
            <td className="n">{usd(d.cash_to_deposit_cents)}</td><td><button onClick={() => setOpen(d)}>View</button></td></tr>))}</tbody></table>
      {days.data?.length === 0 && <div className="empty">No days finished yet.</div>}
      {open && (
        <Modal title={`End of day · ${open.date}`} onClose={() => setOpen(null)} wide footer={<button onClick={() => window.print()}>Print</button>}>
          <table className="grid"><tbody>
            <tr><td>Net sales ({open.statement.sales.count} sales)</td><td className="n">{usd(open.statement.sales.net)}</td></tr>
            <tr><td>Sales tax</td><td className="n">{usd(open.statement.sales.tax)}</td></tr>
            {open.statement.tenders.map((t: any) => <tr key={t.method}><td>Paid by {t.method.replace('_', ' ')}</td><td className="n">{usd(t.cents)}</td></tr>)}
            <tr><td colSpan={2}><CategoryTable categories={open.statement.categories ?? []} sales={open.statement.sales} /></td></tr>
            <tr><td>Lottery sales / payouts</td><td className="n">{usd(open.statement.lottery.sales)} / {usd(open.statement.lottery.payouts)}</td></tr>
            <tr><td>Cash counted</td><td className="n">{usd(open.statement.cashCountedCents)}</td></tr>
            <tr><td>Over / short</td><td className="n">{usd(open.statement.varianceCents)}</td></tr>
            <tr><td>Cash to deposit</td><td className="n">{usd(open.cash_to_deposit_cents)}</td></tr>
          </tbody></table>
          {open.note && <p className="faint">{open.note}</p>}
        </Modal>
      )}
    </div>
  );
}
